import isServer from '@anm/helpers/is/isServer';
import { NextPage, NextPageContext } from 'next';
import { AppProps } from 'next/app';
import { Component, Dispatch } from 'react';
import { hoistStatics, InferableComponentEnhancer } from 'recompose';
import { AnyAction } from 'redux';

import authGuard, { AuthGuardProps } from '../helpers/authGuard';
import sessionGuard from '../helpers/sessionGuard';

type WithAuthGuardProps = Pick<AuthGuardProps, 'redirectTo'>;

const withAuthGuard = ({ redirectTo }: WithAuthGuardProps = {}) =>
  hoistStatics(
    (<P extends object>(WrappedComponent: NextPage<AppProps<P>>) =>
      class WrappedApp extends Component<NextPage<P> & AppProps<P>> {
        static async getInitialProps(props: NextPageContext) {
          const token = authGuard({ redirectTo, ...props });

          const pageProps = WrappedComponent.getInitialProps
            ? await WrappedComponent.getInitialProps.call(WrappedComponent, props)
            : {};

          return { ...pageProps, token };
        }

        constructor(props: { dispatch: Dispatch<AnyAction> } & AppProps<P>) {
          super(props);
          if (isServer()) return;

          const { dispatch } = props;
          sessionGuard(dispatch);
        }

        render() {
          return <WrappedComponent {...this.props} />;
        }
      }) as InferableComponentEnhancer<{}>,
    { getInitialProps: true }
  );

export default withAuthGuard;
